import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    titleColor = "titleColor",
    titleSize = "titleSize",
    titleWeight = "titleWeight",
    subTitle = "subTitle",
    subTitlePosition = "subTitlePosition",
    subTitleColor = "subTitleColor",
    subTitleSize = "subTitleSize",
    subTitleWeight = "subTitleWeight",
    backgroundImage = "backgroundImage",
    titleItalic = "titleItalic",
    subTitleItalic = "subTitleItalic",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: "left" | "center" | "right";
        [fields.titleColor]: string;
        [fields.titleSize]: number;
        [fields.titleWeight]: number;
        [fields.subTitle]: string;
        [fields.subTitlePosition]: "left" | "center" | "right";
        [fields.subTitleColor]: string;
        [fields.subTitleSize]: number;
        [fields.subTitleWeight]: number;
        [fields.backgroundImage]: string;
        [fields.titleItalic]: boolean;
        [fields.subTitleItalic]: boolean;
    };
}

type Props = OwnProps & HasHistory;

export interface HeaderTitleStyles {
    container?: InjectableCss;
    globalTitle?: InjectableCss;
    globalSubTitle?: InjectableCss;
    title?: TypographyProps;
    subTitle?: TypographyProps;
}

export const headerTitleStyles: HeaderTitleStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = headerTitleStyles;

const HeaderTitle: React.FC<Props> = ({ id, fields }) => {
    const containerStyle = {
        css: css`
            ${styles.container?.css || ""}
            background-image: url(${fields.backgroundImage});
            background-repeat: no-repeat;
            gap: 10px;
        `,
    };

    const headerStylesExtention: HeaderTitleStyles = {
        globalTitle: {
            css: css`
                font-family: "Pancetta W05 SemiBold", sans-serif;
                text-transform: uppercase;
                display: inherit;
                width: 100%;
                color: ${fields.titleColor};
                text-align: ${fields.titlePosition};
            `,
        },
        globalSubTitle: {
            css: css`
                display: inherit;
                width: 100%;
                color: ${fields.subTitleColor};
                text-align: ${fields.subTitlePosition};
            `,
        },
        title: {
            size: fields.titleSize,
            weight: fields.titleWeight,
            css: css`
                text-align: ${fields.titlePosition};
                font-style: ${fields.titleItalic ? "italic" : "normal"};
            `,
        },
        subTitle: {
            size: fields.subTitleSize,
            weight: fields.subTitleWeight,
            css: css`
                text-align: ${fields.subTitlePosition};
                font-style: ${fields.titleItalic ? "italic" : "normal"};
            `,
        },
    };

    return (
        <StyledWrapper {...containerStyle}>
            <StyledWrapper {...headerStylesExtention.globalTitle}>
                <Typography {...headerStylesExtention.title}>{parse(fields.title, parserOptions)}</Typography>
            </StyledWrapper>
            <StyledWrapper {...headerStylesExtention.globalSubTitle}>
                <Typography {...headerStylesExtention.subTitle}>{parse(fields.subTitle, parserOptions)}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderTitle,
    definition: {
        group: "Common",
        icon: "HeaderTitle",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.titleColor,
                editorTemplate: "ColorPickerField",
                displayName: "Title Color",
                defaultValue: "black",
            },
            {
                fieldType: "General",
                name: fields.titleSize,
                editorTemplate: "IntegerField",
                defaultValue: 63,
            },
            {
                fieldType: "General",
                name: fields.titleWeight,
                editorTemplate: "IntegerField",
                defaultValue: 0,
            },
            {
                fieldType: "General",
                name: fields.titleItalic,
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                fieldType: "General",
                name: fields.subTitle,
                editorTemplate: "TextField",
                displayName: "subTitle",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.subTitleColor,
                editorTemplate: "ColorPickerField",
                displayName: "Subtitle Color",
                defaultValue: "black",
            },
            {
                fieldType: "General",
                name: fields.subTitleItalic,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.subTitleSize,
                editorTemplate: "IntegerField",
                defaultValue: 15,
            },
            {
                fieldType: "General",
                name: fields.subTitleWeight,
                editorTemplate: "IntegerField",
                defaultValue: 0,
            },
            {
                fieldType: "General",
                name: fields.titlePosition,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                displayName: "Title Position",
                defaultValue: "center",
                hideEmptyOption: true,
            },
            {
                fieldType: "General",
                name: fields.subTitlePosition,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                displayName: "Subtitle Position",
                defaultValue: "center",
            },
            {
                name: fields.backgroundImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
